import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = this.#onQuantityChange.bind(this);
  #swipeCard = null;
  #swipeStartX = 0;
  #swipeStartY = 0;
  #swipeOffsetX = 0;
  #swipeAxis = null;
  #swipePointerId = null;
  #suppressCardClick = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('pointerdown', this.#handleSwipeStart);
    this.addEventListener('pointermove', this.#handleSwipeMove);
    this.addEventListener('pointerup', this.#handleSwipeEnd);
    this.addEventListener('pointercancel', this.#handleSwipeCancel);
    this.addEventListener('dragstart', this.#preventNativeCardDrag);
    this.addEventListener('click', this.#handleCardClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('pointerdown', this.#handleSwipeStart);
    this.removeEventListener('pointermove', this.#handleSwipeMove);
    this.removeEventListener('pointerup', this.#handleSwipeEnd);
    this.removeEventListener('pointercancel', this.#handleSwipeCancel);
    this.removeEventListener('dragstart', this.#preventNativeCardDrag);
    this.removeEventListener('click', this.#handleCardClick, true);
  }

  #handleSwipeStart = (event) => {
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('button, input, quantity-selector-component')) return;

    const card = event.target.closest('[data-cart-swipe-card]');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;

    this.querySelectorAll('[data-cart-swipe-card]').forEach((item) => {
      if (item !== card && item instanceof HTMLElement) item.style.setProperty('--cart-item-swipe-x', '0px');
    });

    this.#swipeCard = card;
    this.#swipePointerId = event.pointerId;
    this.#swipeStartX = event.clientX;
    this.#swipeStartY = event.clientY;
    this.#swipeOffsetX = 0;
    this.#swipeAxis = null;
  };

  #preventNativeCardDrag = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-cart-swipe-card]')) event.preventDefault();
  };

  #handleCardClick = (event) => {
    if (!this.#suppressCardClick || !(event.target instanceof Element)) return;
    if (!event.target.closest('[data-cart-swipe-card]')) return;
    event.preventDefault();
    event.stopPropagation();
    this.#suppressCardClick = false;
  };

  #handleSwipeMove = (event) => {
    if (!(this.#swipeCard instanceof HTMLElement) || event.pointerId !== this.#swipePointerId) return;

    const deltaX = event.clientX - this.#swipeStartX;
    const deltaY = event.clientY - this.#swipeStartY;

    if (!this.#swipeAxis) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 6) return;
      this.#swipeAxis = Math.abs(deltaX) > Math.abs(deltaY) * 1.1 ? 'horizontal' : 'vertical';
    }

    if (this.#swipeAxis === 'vertical') {
      this.#resetSwipe(false);
      return;
    }

    event.preventDefault();
    this.#suppressCardClick = true;
    if (!this.#swipeCard.hasPointerCapture(event.pointerId)) {
      this.#swipeCard.setPointerCapture(event.pointerId);
    }
    this.#swipeOffsetX = Math.max(-96, Math.min(0, deltaX));
    this.#swipeCard.dataset.swipeDragging = 'true';
    this.#swipeCard.style.setProperty('--cart-item-swipe-x', `${this.#swipeOffsetX}px`);
  };

  #handleSwipeEnd = (event) => {
    if (!(this.#swipeCard instanceof HTMLElement) || event.pointerId !== this.#swipePointerId) return;

    const card = this.#swipeCard;
    const line = Number(card.dataset.cartLine);
    if (card.hasPointerCapture(event.pointerId)) card.releasePointerCapture(event.pointerId);
    delete card.dataset.swipeDragging;

    if (this.#swipeAxis === 'horizontal' && this.#swipeOffsetX <= -62 && Number.isInteger(line) && line > 0) {
      event.preventDefault();
      card.style.setProperty('--cart-item-swipe-x', '-100%');
      this.#resetSwipe(true);
      window.setTimeout(() => this.onLineItemRemove(line), 150);
      window.setTimeout(() => (this.#suppressCardClick = false), 0);
      return;
    }

    if (this.#swipeAxis === 'horizontal') event.preventDefault();
    card.style.setProperty('--cart-item-swipe-x', '0px');
    this.#resetSwipe(true);
    window.setTimeout(() => (this.#suppressCardClick = false), 0);
  };

  #handleSwipeCancel = () => {
    if (this.#swipeCard instanceof HTMLElement) {
      delete this.#swipeCard.dataset.swipeDragging;
      this.#swipeCard.style.setProperty('--cart-item-swipe-x', '0px');
    }
    this.#suppressCardClick = false;
    this.#resetSwipe(true);
  };

  #resetSwipe(keepPosition) {
    if (!keepPosition && this.#swipeCard instanceof HTMLElement) {
      delete this.#swipeCard.dataset.swipeDragging;
      this.#swipeCard.style.setProperty('--cart-item-swipe-x', '0px');
    }
    this.#swipeCard = null;
    this.#swipeOffsetX = 0;
    this.#swipeAxis = null;
    this.#swipePointerId = null;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.querySelectorAll('button, input, [name="checkout"]').forEach((control) => {
      if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return;
      if (control.disabled) return;
      control.disabled = true;
      control.dataset.cartMutationDisabled = 'true';
    });
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.querySelectorAll('[data-cart-mutation-disabled]').forEach((control) => {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) control.disabled = false;
      control.removeAttribute('data-cart-mutation-disabled');
    });
    this.querySelectorAll('[data-pending]').forEach((element) => element.removeAttribute('data-pending'));
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
